import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { X, ChevronLeft, ChevronDown, ChevronRight, CornerDownRight, Plus, MessageSquare, Paperclip, History, Repeat } from "lucide-react";
import { Select } from "../ui";
import { Cell } from "./BoardCells";
import UpdatesTab from "./UpdatesTab";
import FilesTab from "./FilesTab";
import ActivityTab from "./ActivityTab";

export default function ItemDrawer({ board, item, members, me, subCols, byPos,
  onClose, onSetName, onSetDescription, onSetCell, onMoveGroup, onAddSubitem, onOpenItem, openColEditor, onSetRecurrence }) {
  const { t } = useApp();
  const [tab, setTab] = useState("updates");
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [subsOpen, setSubsOpen] = useState(true);
  const [newSubName, setNewSubName] = useState("");
  const [desc, setDesc] = useState(item.description || "");
  useEffect(() => { setDesc(item.description || ""); }, [item.id, item.description]);

  const isSub = !!item.parent_id;
  const parent = isSub ? board.items.find((i) => i.id === item.parent_id) : null;
  const schema = isSub ? subCols : board.columns;
  const subs = isSub ? [] : board.items.filter((i) => i.parent_id === item.id).sort(byPos);
  const statusCol = schema.find((c) => c.id === "status");
  const subStatus = (s) => (statusCol?.settings?.options || []).find((o) => o.id === s.values?.status);

  const TABS = [
    { k: "updates", label: t("tab_updates"), Icon: MessageSquare },
    { k: "files", label: t("tab_files"), Icon: Paperclip },
    { k: "activity", label: t("tab_activity"), Icon: History },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-in fade-in" onClick={onClose} data-testid="item-drawer-overlay" />
      <aside className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-background border-l z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200" data-testid="item-drawer">
        {isSub && parent && (
          <button onClick={() => onOpenItem(parent.id)} data-testid="item-drawer-back" title={t("board_back_to_parent")}
            className="flex items-center gap-1.5 px-5 pt-4 text-xs font-semibold text-text-secondary hover:text-primary transition-colors">
            <ChevronLeft className="w-4 h-4" /> <span className="truncate max-w-[380px]">{parent.name}</span>
          </button>
        )}
        <div className="flex items-start justify-between gap-3 p-5 pt-3 border-b">
          <div className="flex-1 min-w-0">
            {isSub && <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1 flex items-center gap-1"><CornerDownRight className="w-3 h-3" />{t("board_subitem")}</div>}
            <input defaultValue={item.name} key={item.id + item.name} onBlur={(e) => onSetName(item, e.target.value)} data-testid="item-drawer-name" className="w-full bg-transparent text-lg font-heading font-medium outline-none" />
          </div>
          <button onClick={onClose} data-testid="item-drawer-close" className="text-text-secondary hover:text-text-primary mt-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Description */}
          <div className="px-5 pt-4 pb-3 border-b">
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={() => onSetDescription(item, desc)}
              data-testid="item-drawer-description" placeholder={t("drawer_description_placeholder")}
              className="w-full bg-surface/60 border rounded-xl p-3 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:border-primary/50 transition-colors resize-y min-h-[72px]" />
          </div>

          {/* Details (collapsible) */}
          <div className="p-5 pb-3 border-b">
            <button onClick={() => setDetailsOpen((o) => !o)} data-testid="item-drawer-details-toggle"
              className="w-full flex items-center gap-2 text-[11px] uppercase tracking-wide font-bold text-text-secondary mb-3 hover:text-text-primary transition-colors">
              {detailsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{t("drawer_details")}
            </button>
            {detailsOpen && (
              <div className="space-y-4">
                {!isSub && (
                  <div>
                    <div className="text-xs text-text-secondary mb-1.5">{t("board_group")}</div>
                    <Select value={item.group_id || board.groups[0]?.id} onChange={(e) => onMoveGroup(item, e.target.value)} data-testid="item-drawer-group">
                      {board.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </Select>
                  </div>
                )}
                {schema.map((c) => (
                  <div key={c.id}>
                    <div className="text-xs text-text-secondary mb-1.5">{c.label}</div>
                    <Cell col={c} value={(item.values || {})[c.id]} onChange={(v) => onSetCell(item, c.id, v)} testid={`drawer-cell-${c.id}`} onEditColumn={(col) => openColEditor(col, isSub ? "sub" : "main")} inDrawer />
                  </div>
                ))}
                {!isSub && onSetRecurrence && (
                  <div>
                    <div className="text-xs text-text-secondary mb-1.5 flex items-center gap-1.5">
                      <Repeat className="w-3.5 h-3.5" />{t("recur_item_label")}
                    </div>
                    <Select value={item.recurrence?.freq || "none"} data-testid="item-drawer-recurrence"
                      onChange={(e) => onSetRecurrence(item, e.target.value === "none" ? null : { freq: e.target.value })}>
                      <option value="none">{t("recur_none")}</option>
                      <option value="daily">{t("recur_daily")}</option>
                      <option value="weekly">{t("recur_weekly")}</option>
                      <option value="monthly">{t("recur_monthly")}</option>
                      <option value="quarterly">{t("recur_quarterly")}</option>
                      <option value="annually">{t("recur_annually")}</option>
                    </Select>
                    {item.recurrence?.freq && (
                      <p className="text-[11px] text-text-secondary mt-1.5">{t("recur_item_hint")}</p>
                    )}
                  </div>
                )}
                {!isSub && (
                  <div className="pt-1">
                    <button onClick={() => setSubsOpen((o) => !o)} data-testid="item-drawer-subitems-toggle" className="w-full flex items-center gap-2 text-[11px] uppercase tracking-wide text-text-secondary mb-2.5 hover:text-text-primary transition-colors">
                      {subsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {t("board_subitems")}
                      {subs.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-hover normal-case tracking-normal font-semibold">{subs.length}</span>}
                    </button>
                    {subsOpen && (
                      <div className="space-y-1.5">
                        {subs.map((s) => {
                          const st = subStatus(s);
                          return (
                            <button key={s.id} onClick={() => onOpenItem(s.id)} data-testid={`item-drawer-subitem-${s.id}`}
                              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border hover:border-primary/50 hover:bg-surface-hover text-left transition-colors group/ds">
                              <CornerDownRight className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                              <span className="flex-1 text-sm truncate">{s.name}</span>
                              {st && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded text-white shrink-0" style={{ backgroundColor: st.color }}>{st.label}</span>}
                              <ChevronRight className="w-3.5 h-3.5 text-text-secondary opacity-0 group-hover/ds:opacity-100 transition-opacity shrink-0" />
                            </button>
                          );
                        })}
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed">
                          <Plus className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                          <input value={newSubName} onChange={(e) => setNewSubName(e.target.value)} placeholder={t("board_add_subitem")} data-testid="item-drawer-add-subitem"
                            onKeyDown={async (e) => { if (e.key === "Enter" && newSubName.trim()) { const v = newSubName.trim(); setNewSubName(""); const d = await onAddSubitem(item, v); if (d) onOpenItem(d.id); } }}
                            className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="sticky top-0 z-10 bg-background flex items-center gap-1 px-4 pt-3 border-b" data-testid="item-drawer-tabs">
            {TABS.map(({ k, label, Icon }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`item-drawer-tab-${k}`}
                className={cn("inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                  tab === k ? "border-primary text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary")}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "updates" && <UpdatesTab itemId={item.id} members={members} me={me} />}
            {tab === "files" && <FilesTab itemId={item.id} me={me} />}
            {tab === "activity" && <ActivityTab itemId={item.id} schema={schema} members={members} />}
          </div>
        </div>
      </aside>
    </>
  );
}
